import React from 'react';
import './PriceChartingWidget.css';
import { formatEastern } from '../../utils/formatDate';
import { priceChartingUrl } from '../../utils/priceCharting';

// "PriceCharting" box with PSA 10/9/8/7 prices and the last-updated time.
// Clicking it opens the product on pricecharting.com (when we have one);
// `onOpen` lets the caller track that click.

const formatDate = (dateString) => {
    if (!dateString) {
        return ''; // Return empty string if dateString is not provided
    }

    try {
        const options = {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        };
        const formattedDate = formatEastern(dateString, options);

        // Split and format
        const [date, timeWithAmpm] = formattedDate.split(', ');
        const [time, ampm] = timeWithAmpm.split(' ');

        return `${date.replace(/\//g, '-')} ${time} ${ampm.toLowerCase()} ET`;
    } catch (error) {
        console.error('Failed to format date:', error);
        return ''; // Return empty string on failure
    }
};

const formatPrice = (v) => (v ? `$${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}` : '--');

// `pending`: the estimate is still being fetched (a just-added cert), so say so
// instead of "No pricing data available".
const PriceChartingWidget = ({ cert, onOpen, pending = false }) => {
    const pricing = cert?.pricing_data;
    const linked = !!(pricing && cert?.price_charting_id);

    const open = () => {
        if (!linked) return;
        onOpen?.();
        window.open(priceChartingUrl(cert), '_blank', 'noopener,noreferrer');
    };

    // When linked, the whole box behaves as one button: hover lift, keyboard
    // focus, Enter/Space to open.
    const linkProps = linked
        ? {
            role: 'link',
            tabIndex: 0,
            'aria-label': 'View on PriceCharting (opens in a new tab)',
            onClick: open,
            onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    open();
                }
            },
        }
        : {};

    return (
        <div className={`pricecharting-widget${linked ? ' is-linked' : ''}`} {...linkProps}>
            <h3>PriceCharting</h3>
            {pricing ? (
                <>
                    {[10, 9, 8, 7].map((g) => (
                        <div key={g} className="price-row">
                            <span className="label">PSA {g}</span>
                            <span className="colon">:</span>
                            <span className="price">{formatPrice(pricing[`psa_${g}`])}</span>
                        </div>
                    ))}
                </>
            ) : (
                <p className={pending ? 'pricecharting-pending' : undefined}>
                    {pending ? 'Getting PriceCharting estimate…' : 'No pricing data available'}
                </p>
            )}
            {pricing?.date_updated && (
                <div className="date-updated">
                    {formatDate(pricing.date_updated)}
                </div>
            )}
            {linked && (
                <div className="pricecharting-cta" aria-hidden="true">
                    View on PriceCharting
                    <svg className="pricecharting-cta-arrow" viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="13 6 19 12 13 18" />
                    </svg>
                </div>
            )}
        </div>
    );
};

export default PriceChartingWidget;
